<!--
    AUTHOR: Ryan Lang
    LATEST UPDATED DATE: 5/31/2024
    DESCRIPTION: Dashboard widget/card that represents an accumulated value according to defined parameter and date/time range values
    ADDITIONAL INFORMATION: //
-->
<template>
              
        <div ref="widgetContent" class="h-full  bg-slate-100/20 p-2 md:p-3">
            
            <div class="view-body h-full flex flex-col justify-between text-center px-1 py-1 xl:px-4 xl:py-4 md:px-3 md:py-2 sm:px-1 sm:py-1 xs:px-1 xs:py-1">
            <div class="text-xs font-normal mb-4">{{ Settings.Comment }}</div>
            <span v-if="noData"  class="text-xl text-slate-500">
                    No Data
            </span>
            <span v-else >
                <div class="param-val text-xl xl:text-2xl md:text-lg sm:text-xl xs:text-xl text-slate-500" v-if="badData">
                  <span class="text-sm">Data Unavailable&nbsp;<InformationCircleIcon
        class="h-4 w-4 text-primary/40 inline hover:cursor-pointer" @click="isInfoModalOpen = true"        
      /></span>
                </div>
                <div class="param-val text-xl xl:text-2xl md:text-lg sm:text-xl xs:text-xl text-slate-500" v-else>
                  {{ returnValue }}
                </div>
                
                
            </span>

            <div v-for="(param,index) in Settings.Parameters" :key="param.param" class="border border-slate-200 bg-white shadow-md rounded p-2 mt-4">
              <span class="param-name break-all text-xs" v-if="param.displayname">
              {{param.displayname}}
              </span> 
              <span class="param-name break-all text-xs" v-else>
              {{param.param}}
              </span>              
            </div>
          </div>  
        </div>  

<!-- Info Modal -->
  <GDialog v-model="isInfoModalOpen" transition="custom-from-bottom-transition">
    <div class="fixed inset-0 overflow-y-auto">
      <div class="flex min-h-full items-center justify-center text-center">
        <div class="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
          <InformationCircleIcon
        class="h-12 w-12 text-primary/40 inline hover:cursor-pointer"></InformationCircleIcon>
          <div class="mt-4">
            
            <p class="text-sm mb-4">{{ badDataDetails}}</p>

            <button
              type="button"
              class="inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium transition text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              @click="isInfoModalOpen = false"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </GDialog>
  <!-- /Info Modal -->
</template>

<script setup>

/****external imports ****/
import { ref, computed, onMounted,watchEffect } from "vue";
import { useStore } from 'vuex' 
import { format,addHours,subMonths,startOfMonth,subYears, startOfYear, endOfYear } from 'date-fns';
import {convertZonedTimetoUTC, getTimeIntervalStart, getTimeIntervalEnd} from '@/scripts/dateHelpers';
import { InformationCircleIcon } from '@heroicons/vue/20/solid';
/****external imports ****/

//component property definitions
const props = defineProps({  
  Settings: { type: Object, required: true },
  ReloadData: Boolean
});

//reactive variable/object definitions
const store = useStore();
const meter = computed(() => {return store.getters.meter});
const userName = computed(() => {return store.state.loggedinEmail.toLowerCase()});
const noData = ref(false);
const badData = ref(false);
const badDataDetails = ref("");
const returnValue = ref();
const isInfoModalOpen = ref(false);
const triggerReload = computed(() => props.ReloadData);

//emit definitions
const emit = defineEmits(['dataReloaded']);

 //check if prop has been set to true, if so reload data.
 watchEffect(async () => {

    if(triggerReload.value)
    {
      await loadAndProcessWidget();

    }
    
});

//returns the start date/time value of the dashboard time interval
function getThisTimeIntervalStart(){

    //type should be "value"

    if(props.Settings.TimeInterval) //predefined time intervals shown to end user in user interface
    {
        return getTimeIntervalStart(props.Settings.TimeInterval)
    }
    else  
        return props.Settings.CustomTimeIntervalStart;  //custom time intervals specified by end user

}


//returns the ending date/time value of the dashboard time interval
function getThisTimeIntervalEnd(){

    if(props.Settings.TimeInterval)
    {
        return getTimeIntervalEnd(props.Settings.TimeInterval)
    }
    else 
        return props.Settings.CustomTimeIntervalEnd;        
    
}


//reutrns array of parameters to be used in api call
function getParams(paramObj) {

let params = [];
for (var p of paramObj) params.push(p.param);

return params;  
} 

//loads the data 
async function loadWidgetData(){
        
    const thisMeterOffset = meter.value.timeZone?.offset
    let thisParams = getParams(props.Settings.Parameters);

    let thisStartDateTime = getThisTimeIntervalStart();
    let thisStartDateTimeUTC = convertZonedTimetoUTC(thisStartDateTime, thisMeterOffset);

    let thisEndDateTime = getThisTimeIntervalEnd();
    let thisEndDateTimeUTC = convertZonedTimetoUTC(thisEndDateTime, thisMeterOffset);

    let thisStartDate = format(thisStartDateTimeUTC, "yyyy-MM-dd");
    let thisStartTime = format(thisStartDateTimeUTC, "HH") + ":" + format(thisStartDateTimeUTC, "mm");
    
    let thisEndDate = format(thisEndDateTimeUTC, "yyyy-MM-dd");
    let thisEndTime = format(thisEndDateTimeUTC, "HH") + ":" + format(thisEndDateTimeUTC, "mm");

    var params = {
    request: "getAccumulatedValue",
    username: userName.value,
    metername: meter.value.name,
    isLatest: props.Settings.TimeInterval == 'current' ? true : false,
    range: { 
        startDate: thisStartDate, 
        startTime: thisStartTime, // UTC TIME!
        endDate: thisEndDate, 
        endTime: thisEndTime // UTC TIME!
    },
    topic: thisParams[0], // projection expression
    };

    let thisr = await store.dispatch("s3_API", params);    
    
    if(thisr.success)
    {

        if(props.Settings.TimeInterval == 'current' || thisParams[0].includes('DemandkWPeak'))    
        {
            
            if(isNaN(thisr.endValue) || thisr.endValue === null || thisr.endValue === undefined){
                badData.value = true;
                badDataDetails.value = "The current value for this parameter is unavailable. This may be due to a lack of data being sent to the cloud for this parameter, or the data may be outside of the expected range.";
            }
            else
                return thisr.endValue;  

        }           
        else{
            

            if(isNaN(thisr.endValue) || isNaN(thisr.startValue)){
                badData.value = true;
                badDataDetails.value = "The accumulated value for this parameter is unavailable. This may be due to a lack of data being sent to the cloud for this parameter, or the data may be outside of the expected range.";
            }
            if(thisr.delta)
                return thisr.delta;  
        }   
        
    }
          
    return null;
    
}

//initial call to load the component contents
function loadAndProcessWidget(){
    
    loadWidgetData().then(res => {
        

        
        if(res !== null)
        {    
            returnValue.value = res;
            noData.value = false;
        }
        else
            noData.value = true;
        
        emit('dataReloaded', true);

    }).catch(err => {

        console.error('Error loading accumulated value widget data:', err);
        noData.value = true;
        emit('dataReloaded', true);

    });
}

onMounted(() => {
    
    
  //call on load
  loadAndProcessWidget();


});

</script>

<style>

</style>